<template>
  <footer class="site-footer">
    <div class="footer-accent"></div>
    <div class="container footer-container">
      <div class="footer-grid">
        <!-- Brand column -->
        <div class="footer-col brand-col">
          <img
            v-if="footerContent.panelLogoSrc"
            :src="footerContent.panelLogoSrc"
            :alt="footerContent.panelLogoAlt"
            class="footer-brand-logo"
          >
          <h5 v-if="brandName" class="footer-brand-name">{{ brandName }}</h5>
          <p v-if="footerContent.panelDescription" class="footer-tagline">
            {{ footerContent.panelDescription }}
          </p>
          <address v-if="addressLines.length" class="footer-address">
            <span v-for="line in addressLines" :key="line">{{ line }}</span>
          </address>
          <div v-if="footerContent.socialLinks?.length" class="footer-social">
            <a
              v-for="social in footerContent.socialLinks"
              :key="social.label"
              :href="social.href"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="social.label"
              class="social-icon"
            >
              <i :class="social.iconClass"></i>
            </a>
          </div>
        </div>

        <!-- Dynamic link columns (first 2 from footerContent.columns) -->
        <div v-for="col in displayColumns" :key="col.title" class="footer-col">
          <h6 class="footer-col-heading">{{ col.title }}</h6>
          <ul class="footer-link-list">
            <li v-for="link in col.links" :key="link.label">
              <router-link v-if="isInternalLink(link.href)" :to="link.href">{{ link.label }}</router-link>
              <a v-else :href="link.href">{{ link.label }}</a>
            </li>
          </ul>
        </div>

        <!-- Contact column -->
        <div class="footer-col">
          <h6 class="footer-col-heading">{{ footerContent.contact?.title || 'Contact' }}</h6>
          <ul class="footer-contact-list">
            <li v-for="item in footerContent.contact?.items || []" :key="item.label">
              <i :class="item.iconClass"></i>
              <a v-if="item.href" :href="item.href">{{ item.label }}</a>
              <span v-else>{{ item.label }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom bar -->
      <div class="footer-bottom">
        <span class="footer-copyright">{{ footerContent.copyright?.text }}</span>
        <span class="footer-credit">
          Powered by
          <a href="https://intendtech.com" target="_blank" rel="noopener">Intend Technosoft Private Limited</a>
          ·
          <a href="https://gopgms.com" target="_blank" rel="noopener">GoPGMS</a>
        </span>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useAccountStore } from '@/stores/account'
import { resolveFlavor } from '@/config/flavors'
import { resolveFooterContent } from '@/data/footerContent'

const flavor = inject('flavor', resolveFlavor())
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''

/**
 * Build footer content from API-returned footer data + flavor.
 * Maps the API shape to the template-expected shape.
 */
const buildApiFooterContent = (flav, apiFooter) => {
  // Map API columns: { heading, links[{ label, route }] }
  // to template: { title, links[{ label, href }] }
  const columns = (apiFooter.columns || []).map((col) => ({
    title: col.heading || '',
    links: (col.links || []).map((l) => ({ label: l.label, href: l.route || l.href || '#' }))
  }))

  // Build contact items from flavor.contact
  const contactItems = []
  const c = flav.contact || {}
  if (c.phone?.display || c.phone?.value) {
    contactItems.push({
      label: c.phone.display || c.phone.value,
      iconClass: 'bi bi-telephone',
      href: `tel:${c.phone.value}`
    })
  }
  if (c.email?.value) {
    contactItems.push({
      label: c.email.value,
      iconClass: 'bi bi-envelope',
      href: `mailto:${c.email.value}`
    })
  }
  if (c.whatsapp?.display || c.whatsapp?.value) {
    contactItems.push({
      label: c.whatsapp.display || c.whatsapp.value,
      iconClass: 'bi bi-whatsapp',
      href: `https://wa.me/${(c.whatsapp.value || '').replace(/[^\d]/g, '')}`
    })
  }

  return {
    panelLogoSrc: flav.logos.footer,
    panelLogoAlt: flav.displayName,
    panelDescription: apiFooter.tagline || flav.displayName,
    socialLinks: flav.socialLinks || [],
    columns,
    contact: { title: 'Contact Us', items: contactItems },
    copyright: {
      year: new Date().getFullYear(),
      text: apiFooter.copyright || `© ${new Date().getFullYear()} ${flav.displayName}`
    },
    paymentMethods: {
      title: 'Secure Payments',
      methods: apiFooter.paymentMethods || []
    },
    flavorKey: flav.key
  }
}

const footerContent = computed(() => {
  // If the API returned footer data (merged onto the flavor), use it
  if (flavor?._apiFooter) {
    return buildApiFooterContent(flavor, flavor._apiFooter)
  }
  // Fall back to hardcoded per-flavor config
  return resolveFooterContent(hostname || flavor?.domains?.[0] || '')
})

const accountStore = useAccountStore()

const brandName = computed(() => flavor?.displayName || '')

const primaryBranch = computed(() => accountStore.branches?.[0] ?? null)

const addressLines = computed(() => {
  if (!primaryBranch.value) return []
  const lines = []
  if (primaryBranch.value.address) lines.push(primaryBranch.value.address)
  const cityState = [primaryBranch.value.city, primaryBranch.value.state].filter(Boolean).join(' - ')
  if (cityState) lines.push(cityState)
  return lines
})

// Show first 2 link columns from footerContent (brand col + contact col already take 2 slots)
const displayColumns = computed(() => {
  const cols = footerContent.value.columns || []
  return cols.slice(0, 2)
})

const isInternalLink = (href) => {
  if (!href) return false
  return href.startsWith('/') && !href.startsWith('//')
}
</script>

<style scoped>
.site-footer {
  background: #1e2530;
  color: #c8cdd3;
}

.footer-accent {
  height: 3px;
  background: var(--primary-color1, #0d6efd);
}

.footer-container {
  padding: 60px 0 0;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 40px;
  padding-bottom: 48px;
}

/* ── Brand column ────────────────────────────────────────── */
.brand-col {
  padding-right: 20px;
}

.footer-brand-logo {
  display: block;
  max-width: 120px;
  max-height: 48px;
  object-fit: contain;
  margin-bottom: 16px;
}

.footer-brand-name {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px;
}

.footer-tagline {
  font-size: 14px;
  line-height: 1.6;
  color: #9aa0a8;
  margin: 0 0 16px;
}

.footer-address {
  font-style: normal;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: #9aa0a8;
  margin-bottom: 16px;
}

.footer-social {
  display: flex;
  gap: 10px;
}

.social-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #333d4a;
  color: #fff;
  font-size: 14px;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
}

.social-icon:hover {
  background: var(--primary-color1, #0d6efd);
  color: #fff;
}

/* ── Column headings ─────────────────────────────────────── */
.footer-col-heading {
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 20px;
}

/* ── Link lists ──────────────────────────────────────────── */
.footer-link-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.footer-link-list a {
  color: #9aa0a8;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.2s;
}

.footer-link-list a:hover {
  color: #fff;
}

/* ── Contact list ────────────────────────────────────────── */
.footer-contact-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.footer-contact-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
}

.footer-contact-list li i {
  color: var(--primary-color1, #0d6efd);
  font-size: 15px;
  margin-top: 2px;
  flex-shrink: 0;
}

.footer-contact-list li a,
.footer-contact-list li span {
  color: #9aa0a8;
  text-decoration: none;
  transition: color 0.2s;
}

.footer-contact-list li a:hover {
  color: #fff;
}

/* ── Bottom bar ──────────────────────────────────────────── */
.footer-bottom {
  border-top: 1px solid #2d3640;
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.footer-copyright {
  font-size: 13px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.footer-credit {
  font-size: 12px;
  color: #6b7280;
}

.footer-credit a {
  color: #9aa0a8;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: text-decoration-color 0.2s;
}

.footer-credit a:hover {
  text-decoration-color: #9aa0a8;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 991px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }

  .brand-col {
    grid-column: 1 / -1;
    padding-right: 0;
  }
}

@media (max-width: 575px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .footer-container {
    padding: 40px 0 0;
  }

  .footer-bottom {
    flex-direction: column;
    text-align: center;
  }
}
</style>
